import { InfoCircleOutlined, EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Table,
  Tooltip,
  Typography,
  Space,
  Tag,
} from "antd";
import React, { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addSingleWeeklyHoliday,
  loadAllWeeklyHoliday,
} from "../../redux/rtk/features/weeklyHoliday/weeklyHolidaySlice";
import ViewBtn from "../Buttons/ViewBtn";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import { getAllMaster } from "../../redux/rtk/features/masters/masterSlice";

function CustomTable({ list, loading, onEdit }) {
  const [columnsToShow, setColumnsToShow] = useState([]);

  const columns = [
    {
      id: 1,
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
    },
    {
      id: 2,
      title: "Name",
      dataIndex: "WeeklyWorkingDaysName",
      key: "WeeklyWorkingDaysName",
    },
    {
      id: 3,
      title: "Week Start Day",
      dataIndex: "StartDayName",
      key: "StartDayName",
    },
    {
      id: 4,
      title: "Week End Day",
      dataIndex: "EndDayName",
      key: "EndDayName",
    },
    {
      id: 5,
      title: "Special Week Off",
      dataIndex: "SpecialWeekOffNames",
      key: "SpecialWeekOffNames",
      render: (text) => text || "-",
    },
    {
      id: 6,
      title: "Special Days Off",
      dataIndex: "SpecialDaysOffNames",
      key: "SpecialDaysOffNames",
      render: (text) => text || "-",
    },
    {
      id: 7,
      title: "Action",
      dataIndex: "WeeklyWorkingDaysId",
      key: "action",
      render: (id, record) => (
        <Space>
          {/* <ViewBtn path={`/admin/holiday/week/${id}/`} /> */}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) =>
    arr.map((i, index) => ({ ...i, key: i.WeeklyWorkingDaysId, serial: index + 1 }));

  return (
    <Card>
      <div className="text-center my-2 flex justify-between">
        <h5 className="department-list-title text-color-2 text-xl mb-2">
          Weekly Working Day List
        </h5>
        {list && (
          <div>
            <CsvLinkBtn>
              <CSVLink
                data={list}
                className="btn btn-dark btn-sm mb-1"
                filename="weekly-holiday"
              >
                Download CSV
              </CSVLink>
            </CsvLinkBtn>
          </div>
        )}
      </div>

      {list && (
        <div style={{ marginBottom: "30px" }}>
          <ColVisibilityDropdown
            options={columns}
            columns={columns}
            columnsToShowHandler={columnsToShowHandler}
          />
        </div>
      )}

      <Table
        scroll={{ x: true }}
        loading={!list || loading}
        columns={columnsToShow}
        dataSource={list ? addKeys(list) : []}
      />
    </Card>
  );
}

const AddWeeklyHoliday = ({ drawer }) => {
  const { list, loading } = useSelector((state) => state.weeklyHoliday);
  const masterData = useSelector((state) => state.masters.masters);
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentWeeklyWorkingDaysId, setCurrentWeeklyWorkingDaysId] = useState(0);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllWeeklyHoliday());
    dispatch(getAllMaster());
  }, [dispatch]);

  useEffect(() => {
    if (masterData && masterData.WeeklyDayMaster && masterData.WeeklyNumberMaster) {
      setDaysOfWeek(masterData.WeeklyDayMaster);
      setWeekOptions(masterData.WeeklyNumberMaster);
    }
  }, [masterData]);

  const handleEdit = (record) => {
    setIsEditMode(true);
    setCurrentWeeklyWorkingDaysId(record.WeeklyWorkingDaysId);
    
    // Parse comma separated IDs to arrays for multi-select fields
    const specialWeekOffIds = record.SpecialWeekOffIds ? 
      record.SpecialWeekOffIds.split(',').map(id => parseInt(id.trim())) : 
      [];
      
    const specialDaysOffIds = record.SpecialDaysOffIds ? 
      record.SpecialDaysOffIds.split(',').map(id => parseInt(id.trim())) : 
      [];

    form.setFieldsValue({
      WeeklyWorkingDaysName: record.WeeklyWorkingDaysName,
      StartDayId: record.StartDayId,
      EndDayId: record.EndDayId,
      SpecialWeekOffIds: specialWeekOffIds,
      SpecialDaysOffIds: specialDaysOffIds,
    });
  };

  const resetForm = () => {
    form.resetFields();
    setIsEditMode(false);
    setCurrentWeeklyWorkingDaysId(0);
  };

  const onFinish = async (values) => {
    setLoader(true);
    const payload = {
      WeeklyWorkingDaysId: currentWeeklyWorkingDaysId,
      WeeklyWorkingDaysName: values.WeeklyWorkingDaysName,
      StartDayId: values.StartDayId,
      EndDayId: values.EndDayId,
      SpecialWeekOffIds: values.SpecialWeekOffIds || [],
      SpecialDaysOffIds: values.SpecialDaysOffIds || [],
      DataStatus: 1,
    };

    const resp = await dispatch(addSingleWeeklyHoliday(payload));

    if (resp.payload.message === "success") {
      setLoader(false);
      resetForm();
      dispatch(loadAllWeeklyHoliday());
      toast.success(isEditMode ? "Weekly holiday updated successfully" : "Weekly holiday added successfully");
    } else {
      setLoader(false);
      toast.error(isEditMode ? "Failed to update weekly holiday" : "Failed to add weekly holiday");
    }
  };

  const onFinishFailed = () => {
    toast.warning("Please fill all required fields correctly");
    setLoader(false);
  };

  // Function to get item name by ID
  const getWeekNameById = (id) => {
    const week = weekOptions.find(w => w.WeeklyNumberId === id);
    return week ? week.WeeklyNumberName : `Week ${id}`;
  };

  const getDayNameById = (id) => {
    const day = daysOfWeek.find(d => d.WeeklyDayId === id);
    return day ? day.WeeklyDayName : `Day ${id}`;
  };

  // Custom tag render for select components
  const tagRender = (props, getNameFunc) => {
    const { label, value, closable, onClose } = props;
    const displayName = getNameFunc(value);
    
    return (
      <Tag 
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {displayName}
      </Tag>
    );
  };

  const { Title } = Typography;

  return (
    <div className="container mx-auto">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <Card bordered={true} className="h-full">
            <div className="text-center mb-4">
              <Title level={4}>
                {isEditMode ? "Edit Weekly Work Days" : "Add Weekly Work Days"}
              </Title>
            </div>
            <Form
              form={form}
              name="weeklyWorkDaysForm"
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label={<span className="required-label">Name:</span>}
                name="WeeklyWorkingDaysName"
                rules={[
                  {
                    required: true,
                    message: "Please input name!",
                  },
                ]}
              >
                <Input placeholder="Saturday-Friday" />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Start Day
                    <Tooltip title="Select working week start day">
                      <InfoCircleOutlined style={{ marginLeft: "5px" }} />
                    </Tooltip>
                  </span>
                }
                name="StartDayId"
                rules={[
                  {
                    required: true,
                    message: "Please input start day!",
                  },
                ]}
              >
                <Select
                  loading={!daysOfWeek}
                  showSearch
                  placeholder="Select Start Day"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {daysOfWeek &&
                    daysOfWeek.map((acc) => (
                      <Select.Option
                        key={acc.WeeklyDayId}
                        value={acc.WeeklyDayId}
                      >
                        {acc.WeeklyDayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    End Day
                    <Tooltip title="Select working week end day">
                      <InfoCircleOutlined style={{ marginLeft: "5px" }} />
                    </Tooltip>
                  </span>
                }
                name="EndDayId"
                rules={[
                  {
                    required: true,
                    message: "Please input end day!",
                  },
                ]}
              >
                <Select
                  loading={!daysOfWeek}
                  showSearch
                  placeholder="Select End Day"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {daysOfWeek &&
                    daysOfWeek.map((acc) => (
                      <Select.Option
                        key={acc.WeeklyDayId}
                        value={acc.WeeklyDayId}
                      >
                        {acc.WeeklyDayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Special Off Week
                    <Tooltip title="Select week number for special off">
                      <InfoCircleOutlined style={{ marginLeft: "5px" }} />
                    </Tooltip>
                  </span>
                }
                name="SpecialWeekOffIds"
              >
                <Select
                  loading={!weekOptions}
                  showSearch
                  mode="multiple"
                  placeholder="Select Weeks"
                  optionFilterProp="children"
                  tagRender={(props) => tagRender(props, getWeekNameById)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {weekOptions &&
                    weekOptions.map((acc) => (
                      <Select.Option
                        key={acc.WeeklyNumberId}
                        value={acc.WeeklyNumberId}
                      >
                        {acc.WeeklyNumberName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Special Off Day
                    <Tooltip title="Select days for special off">
                      <InfoCircleOutlined style={{ marginLeft: "5px" }} />
                    </Tooltip>
                  </span>
                }
                name="SpecialDaysOffIds"
              >
                <Select
                  loading={!daysOfWeek}
                  showSearch
                  mode="multiple"
                  placeholder="Select Days"
                  optionFilterProp="children"
                  tagRender={(props) => tagRender(props, getDayNameById)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {daysOfWeek &&
                    daysOfWeek.map((acc) => (
                      <Select.Option
                        key={acc.WeeklyDayId}
                        value={acc.WeeklyDayId}
                      >
                        {acc.WeeklyDayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    block
                    loading={loader}
                  >
                    {isEditMode ? "Update Weekly Holiday" : "Add Weekly Holiday"}
                  </Button>
                  {isEditMode && (
                    <Button size="large" onClick={resetForm}>
                      Cancel
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} sm={24} md={16} lg={16} xl={16}>
          <CustomTable list={list} loading={loading} onEdit={handleEdit} />
        </Col>
      </Row>
    </div>
  );
};

export default AddWeeklyHoliday;